import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined, ReloadOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import { MapContainer, Marker, TileLayer, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { QRCodeCanvas } from 'qrcode.react';
import {
  UpdateConfigInput,
  useTenantAdminConfig,
  useUpdateTenantConfig,
} from '@shared/api/hooks/useTenantAdminConfig';
import { useCheckinLog, useGenerateMatchQr } from '@shared/api/hooks/useCheckin';
import { useMatches } from '@shared/api/hooks/useMatches';

// Leaflet marker icons fix (default URLs broken under Vite)
const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

interface FormValues {
  stadiumLat: number;
  stadiumLon: number;
  checkinRadiusM: number;
  checkinQrMode: 'permanent' | 'per_match';
  checkinWindowMinutesBefore: number;
  checkinWindowMinutesAfter: number;
  checkinRejectMockedGps: boolean;
  // Punti del check-in (T-267): spostati qui da Impostazioni, che ora non
  // gestisce piu' il check-in. Sono le uniche fonti di questi due valori.
  checkinPointsWithGeo: number;
  checkinPointsWithoutGeo: number;
}

function MapRecenter({ lat, lon }: { lat: number; lon: number }): null {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lon]);
  }, [lat, lon, map]);
  return null;
}

export function CheckinPage(): React.ReactElement {
  const [form] = Form.useForm<FormValues>();
  const { data: cfg, isLoading, refetch, isFetching } = useTenantAdminConfig();
  const updateMutation = useUpdateTenantConfig();
  const { data: log, refetch: refetchLog } = useCheckinLog(50);
  const matches = useMatches({ status: 'SCHEDULED', limit: 20 });
  const genQr = useGenerateMatchQr();

  const [pos, setPos] = useState<{ lat: number; lon: number }>({ lat: 42.6597, lon: 13.7042 });
  const [radius, setRadius] = useState(500);
  const [qrMode, setQrMode] = useState<'permanent' | 'per_match'>('permanent');
  const [address, setAddress] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [matchQrPayload, setMatchQrPayload] = useState<string | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cfg) {
      const lat = cfg.stadiumLat ?? 42.6597;
      const lon = cfg.stadiumLon ?? 13.7042;
      setPos({ lat, lon });
      setRadius(cfg.checkinRadiusM);
      setQrMode((cfg.checkinQrMode === 'per_match' ? 'per_match' : 'permanent'));
      form.setFieldsValue({
        stadiumLat: lat,
        stadiumLon: lon,
        checkinRadiusM: cfg.checkinRadiusM,
        checkinQrMode: cfg.checkinQrMode === 'per_match' ? 'per_match' : 'permanent',
        checkinWindowMinutesBefore: cfg.checkinWindowMinutesBefore,
        checkinWindowMinutesAfter: cfg.checkinWindowMinutesAfter,
        checkinRejectMockedGps: cfg.checkinRejectMockedGps,
        checkinPointsWithGeo: cfg.checkinPointsWithGeo,
        checkinPointsWithoutGeo: cfg.checkinPointsWithoutGeo,
      });
    }
  }, [cfg, form]);

  // cfg.tenantId is the tenant UUID (FK). The QR scheme expects the slug
  // (matched by backend against X-Tenant-ID header in checkin.service.ts).
  const tenantSlug = (import.meta.env.VITE_TENANT_SLUG as string | undefined) ?? 'teramo';
  const permanentPayload = useMemo(
    () => `teramocalcio://checkin/permanent/${tenantSlug}`,
    [tenantSlug],
  );

  const onSubmit = async (): Promise<void> => {
    const v = await form.validateFields();
    try {
      const input: UpdateConfigInput = {
        stadiumLat: v.stadiumLat,
        stadiumLon: v.stadiumLon,
        checkinRadiusM: v.checkinRadiusM,
        checkinQrMode: v.checkinQrMode,
        checkinWindowMinutesBefore: v.checkinWindowMinutesBefore,
        checkinWindowMinutesAfter: v.checkinWindowMinutesAfter,
        checkinRejectMockedGps: v.checkinRejectMockedGps,
        checkinPointsWithGeo: v.checkinPointsWithGeo,
        checkinPointsWithoutGeo: v.checkinPointsWithoutGeo,
      };
      await updateMutation.mutateAsync(input);
      message.success('Configurazione check-in salvata.');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : msg ?? 'Errore');
    }
  };

  const onGeocode = async (): Promise<void> => {
    if (!address.trim()) return;
    setGeocoding(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
        address,
      )}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'it' } });
      const json = (await res.json()) as Array<{ lat: string; lon: string }>;
      if (!json.length) {
        message.warning('Indirizzo non trovato');
        return;
      }
      const lat = parseFloat(json[0].lat);
      const lon = parseFloat(json[0].lon);
      setPos({ lat, lon });
      form.setFieldsValue({ stadiumLat: lat, stadiumLon: lon });
    } catch {
      message.error('Errore geocoding');
    } finally {
      setGeocoding(false);
    }
  };

  const downloadQr = (filename: string): void => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const onGenerateMatchQr = async (): Promise<void> => {
    if (!selectedMatchId) return;
    try {
      const res = await genQr.mutateAsync(selectedMatchId);
      setMatchQrPayload(res.qrPayload);
    } catch {
      message.error('Errore generazione QR per partita');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <Skeleton active />
      </Card>
    );
  }

  const activeQrPayload = qrMode === 'permanent' ? permanentPayload : matchQrPayload;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Check-in stadio
            </Typography.Title>
            <Tag color="blue">v{cfg?.configVersion ?? '?'}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Aggiorna
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={onSubmit}
              loading={updateMutation.isPending}
            >
              Salva
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          message="La finestra di check-in è relativa alla data della partita. Esempio: 60min prima → 120min dopo significa scan attivo da 1h prima del calcio d'inizio fino a 2h dopo."
          style={{ marginBottom: 16 }}
        />
        <Row gutter={16}>
          <Col span={12}>
            <Card type="inner" title="Posizione stadio">
              <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
                <Input
                  placeholder="Cerca indirizzo (es. Stadio Bonolis Teramo)"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onPressEnter={onGeocode}
                />
                <Button icon={<SearchOutlined />} onClick={onGeocode} loading={geocoding}>
                  Cerca
                </Button>
              </Space.Compact>
              <div style={{ height: 360, borderRadius: 6, overflow: 'hidden' }}>
                <MapContainer
                  center={[pos.lat, pos.lon]}
                  zoom={16}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap"
                  />
                  <MapRecenter lat={pos.lat} lon={pos.lon} />
                  <Marker
                    position={[pos.lat, pos.lon]}
                    icon={markerIcon}
                    draggable
                    eventHandlers={{
                      dragend: (e) => {
                        const ll = (e.target as L.Marker).getLatLng();
                        setPos({ lat: ll.lat, lon: ll.lng });
                        form.setFieldsValue({ stadiumLat: ll.lat, stadiumLon: ll.lng });
                      },
                    }}
                  />
                  <Circle center={[pos.lat, pos.lon]} radius={radius} pathOptions={{ color: '#AC0000' }} />
                </MapContainer>
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card type="inner" title="Parametri check-in">
              <Form form={form} layout="vertical">
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="stadiumLat" label="Latitudine" rules={[{ required: true }]}>
                      <InputNumber
                        step={0.000001}
                        style={{ width: '100%' }}
                        onChange={(v) =>
                          v !== null && setPos((p) => ({ ...p, lat: v as number }))
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="stadiumLon" label="Longitudine" rules={[{ required: true }]}>
                      <InputNumber
                        step={0.000001}
                        style={{ width: '100%' }}
                        onChange={(v) =>
                          v !== null && setPos((p) => ({ ...p, lon: v as number }))
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="checkinRadiusM"
                  label="Raggio (m)"
                  rules={[{ required: true }]}
                  extra="Default 500m. Min 50, max 5000."
                >
                  <InputNumber
                    min={50}
                    max={5000}
                    step={50}
                    style={{ width: '100%' }}
                    onChange={(v) => v !== null && setRadius(v as number)}
                  />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="checkinWindowMinutesBefore"
                      label="Apertura (min prima)"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={0} max={720} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="checkinWindowMinutesAfter"
                      label="Chiusura (min dopo)"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={0} max={720} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="checkinQrMode"
                  label="Modalità QR"
                  rules={[{ required: true }]}
                  extra="Permanente: un solo QR per stadio. Per match: QR univoco per partita."
                >
                  <Select
                    onChange={(v) => setQrMode(v as 'permanent' | 'per_match')}
                    options={[
                      { value: 'permanent', label: 'Permanente (raccomandato)' },
                      { value: 'per_match', label: 'Per partita' },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name="checkinRejectMockedGps"
                  label="Rifiuta GPS finto"
                  valuePropName="checked"
                  extra="Blocca check-in se Android segnala mock-location attiva (Fake GPS apps)."
                >
                  <Switch />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="checkinPointsWithGeo"
                      label="Punti con verifica GPS"
                      rules={[{ required: true, type: 'number', min: 1, max: 500 }]}
                      extra="Punti quando la posizione GPS conferma la presenza allo stadio."
                    >
                      <InputNumber min={1} max={500} precision={0} style={{ width: '100%' }} addonAfter="pt" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="checkinPointsWithoutGeo"
                      label="Punti senza GPS"
                      rules={[{ required: true, type: 'number', min: 1, max: 500 }]}
                      extra="Punti quando il check-in avviene solo via QR, senza verifica geo."
                    >
                      <InputNumber min={1} max={500} precision={0} style={{ width: '100%' }} addonAfter="pt" />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>
        </Row>
      </Card>

      <Card title="QR Code">
        {qrMode === 'permanent' ? (
          <Space direction="vertical">
            <Typography.Text>
              QR permanente del tenant <Tag>{tenantSlug}</Tag>. Stampalo e affiggilo allo stadio.
            </Typography.Text>
            <div ref={qrRef}>
              <QRCodeCanvas value={permanentPayload} size={240} includeMargin />
            </div>
            <Typography.Text type="secondary" copyable={{ text: permanentPayload }}>
              {permanentPayload}
            </Typography.Text>
            <Button icon={<DownloadOutlined />} onClick={() => downloadQr(`qr-${tenantSlug}.png`)}>
              Scarica PNG
            </Button>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Select
                style={{ minWidth: 380 }}
                placeholder="Seleziona partita"
                value={selectedMatchId}
                onChange={(v) => {
                  setSelectedMatchId(v);
                  setMatchQrPayload(null);
                }}
                options={(matches.data?.data ?? []).map((m) => ({
                  value: m.id,
                  label: `${new Date(m.date).toLocaleDateString('it-IT')} — ${m.homeTeamName} vs ${m.awayTeamName}`,
                }))}
              />
              <Button
                type="primary"
                onClick={onGenerateMatchQr}
                loading={genQr.isPending}
                disabled={!selectedMatchId}
              >
                Genera QR
              </Button>
            </Space>
            {matchQrPayload && (
              <>
                <div ref={qrRef}>
                  <QRCodeCanvas value={matchQrPayload} size={240} includeMargin />
                </div>
                <Typography.Text type="secondary" copyable={{ text: matchQrPayload }}>
                  {matchQrPayload}
                </Typography.Text>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => downloadQr(`qr-match-${selectedMatchId}.png`)}
                >
                  Scarica PNG
                </Button>
              </>
            )}
            {!matchQrPayload && (
              <Typography.Text type="secondary">
                Seleziona partita e clicca &quot;Genera QR&quot;.
              </Typography.Text>
            )}
          </Space>
        )}
        {!activeQrPayload && qrMode === 'permanent' && (
          <Alert type="warning" message="Tenant slug non disponibile" showIcon />
        )}
      </Card>

      <Card
        title="Ultimi check-in"
        extra={<Button icon={<ReloadOutlined />} onClick={() => refetchLog()}>Aggiorna</Button>}
      >
        <Table
          dataSource={log ?? []}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: 'Data',
              dataIndex: 'createdAt',
              render: (v: string) => new Date(v).toLocaleString('it-IT'),
              width: 170,
            },
            { title: 'Tifoso', dataIndex: 'userDisplayName' },
            { title: 'Tessera', dataIndex: 'fanCardCode', width: 110 },
            { title: 'Partita', dataIndex: 'matchLabel' },
            {
              title: 'Geo',
              dataIndex: 'isGeoValid',
              width: 80,
              render: (v: boolean) => <Tag color={v ? 'green' : 'orange'}>{v ? 'OK' : 'NO'}</Tag>,
            },
            {
              title: 'Mock',
              dataIndex: 'clientMockedGps',
              width: 80,
              render: (v: boolean) =>
                v ? <Tag color="red">FAKE</Tag> : <Tag color="default">—</Tag>,
            },
            {
              title: 'Punti',
              dataIndex: 'pointsAwarded',
              width: 80,
              render: (v: number) => <Tag color="blue">+{v}</Tag>,
            },
          ]}
        />
        <Divider />
        <Typography.Text type="secondary">
          Mostra ultimi 50. I check-in con badge &quot;FAKE&quot; sono stati comunque accettati (rifiuto disattivo o tifoso pre-fix).
        </Typography.Text>
      </Card>
    </Space>
  );
}
